/**
 * @file ReportTable.tsx
 * @brief Shared table shell for WEBPANEL Reports (Phase C).
 *
 * @details
 * Headers accept per-column `align` so numeric/date columns match body
 * `text-right` cells (27-07-2026 alignment fix). Plain strings stay left.
 * Default `variant="cell"` is flush for GridBlock section grids; `card`
 * keeps the older rounded border box when needed.
 *
 * @author DES
 * @date 27-07-2026
 * @updated 29-07-2026 — cell variant for flush grid reports
 */

import { cn } from "@/shared/theme/cn";

export type ReportTableColumn = {
  label: string;
  align?: "left" | "right";
};

type ReportTableProps = {
  headers: Array<string | ReportTableColumn>;
  isLoading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** `cell` (default) — flush inside a GridCell; `card` — rounded bordered box. */
  variant?: "cell" | "card";
};

function normalizeHeader(
  header: string | ReportTableColumn,
): { label: string; align: "left" | "right" } {
  if (typeof header === "string") {
    return { label: header, align: "left" };
  }
  return { label: header.label, align: header.align ?? "left" };
}

export function ReportTable({
  headers,
  isLoading = false,
  empty = false,
  emptyMessage = "No rows for this range yet.",
  children,
  footer,
  variant = "cell",
}: ReportTableProps) {
  if (isLoading) {
    return (
      <p className="px-4 py-10 text-center text-sm text-muted-foreground md:px-5">
        Loading…
      </p>
    );
  }
  if (empty) {
    return (
      <p className="px-4 py-10 text-center text-sm text-muted-foreground md:px-5">
        {emptyMessage}
      </p>
    );
  }

  const cols = headers.map(normalizeHeader);
  const isCell = variant === "cell";

  return (
    <div
      className={cn(
        "overflow-hidden",
        isCell ? undefined : "rounded-2xl border",
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {cols.map((col, index) => (
                <th
                  key={`${col.label}-${index}`}
                  className={cn(
                    "px-4 py-2 font-medium",
                    col.align === "right" ? "text-right" : "text-left",
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">{children}</tbody>
          {footer ? (
            <tfoot className="border-t border-border/60 bg-secondary/20 font-medium">
              {footer}
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
